import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import LandingPreviewDocument from './LandingPreviewDocument';
import getLandingPreviewCss from './landingPreviewCss';
import blackFontUrl from '../assets/fonts/sofia-pro-black.woff2';
import semiboldFontUrl from '../assets/fonts/sofia-pro-semibold.woff2';

const configShape = PropTypes.shape({
  backgroundImageUrl: PropTypes.string,
  overlay: PropTypes.oneOf(['text', 'image']).isRequired,
  text: PropTypes.string,
  overlayImageUrl: PropTypes.string,
  logoColor: PropTypes.string.isRequired,
  showAnnouncementBanner: PropTypes.bool,
  primaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
  secondaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
});

const LandingPreviewFrame = forwardRef(({ width, height, config }, ref) => {
  const iframeRef = useRef(null);
  const mountNodeRef = useRef(null);
  const configRef = useRef(config);
  configRef.current = config;

  useImperativeHandle(ref, () => ({
    getCaptureNode: () => iframeRef.current?.contentDocument?.documentElement ?? null,
  }), []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const doc = iframe.contentDocument;
    if (!doc) return undefined;

    const css = getLandingPreviewCss({ blackFontUrl, semiboldFontUrl });
    doc.open();
    doc.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>${css}</style>
</head>
<body>
  <div id="root"></div>
</body>
</html>`);
    doc.close();

    mountNodeRef.current = doc.getElementById('root');

    if (mountNodeRef.current) {
      ReactDOM.render(
        <LandingPreviewDocument config={configRef.current} />,
        mountNodeRef.current,
      );
    }

    return () => {
      if (mountNodeRef.current) {
        ReactDOM.unmountComponentAtNode(mountNodeRef.current);
        mountNodeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mountNodeRef.current) return;
    ReactDOM.render(
      <LandingPreviewDocument config={config} />,
      mountNodeRef.current,
    );
  }, [config]);

  return (
    <iframe
      ref={iframeRef}
      title="Landing page preview"
      width={width}
      height={height}
      className="landing-page-simulator__iframe"
      style={{
        width,
        height,
        border: 'none',
        display: 'block',
        background: '#fff',
      }}
    />
  );
});

LandingPreviewFrame.displayName = 'LandingPreviewFrame';

LandingPreviewFrame.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  config: configShape.isRequired,
};

export default LandingPreviewFrame;
